import { getChainConfig } from "@/config/contracts";
import { getTokenConfigs } from "@/config/tokens";
import { debugCollectionAllowed, debugLog } from "@/lib/debugLog";
import { useDebugStore, type DebugLogEntry } from "@/stores/useDebugStore";

/**
 * Debug-panel view of which contracts a flow actually called.
 *
 * The "── x started ──" line is an intention: it is written before the flow
 * knows whether the quest is paid. A volunteer ($0) quest then skips every
 * contract, and the panel used to say nothing either way. The summary written
 * here is built from the `chain` entries PolygonClient's onSend emits per
 * confirmed tx — effects, not intentions — so "contracts called: none" is
 * something the panel observed, not something the UI assumed.
 */

export interface ContractEntry {
  label: string;
  address: string;
}

export interface ContractCall {
  contract: string;
  method: string;
  txHash: string;
}

/** Protocol contracts, in the order the Contracts tab lists them. */
export function protocolContracts(): ContractEntry[] {
  const c = getChainConfig().contracts;
  return [
    { label: "QuestManager", address: c.questManager },
    { label: "TaskManager", address: c.taskManager },
    { label: "BountyBoard", address: c.bountyBoard },
    { label: "EscrowVault", address: c.escrowVault },
    { label: "UserRegistry", address: c.userRegistry },
    { label: "ReputationLedger", address: c.reputationLedger },
    { label: "DisputeResolver", address: c.disputeResolver },
  ];
}

export function tokenContracts(): ContractEntry[] {
  return [...getTokenConfigs()].map(([symbol, config]) => ({
    label: symbol,
    address: config.address,
  }));
}

/** Name for a tx target, or the raw address when it is not a known contract. */
export function contractLabelFor(
  address: string,
  directory: ContractEntry[] = [...protocolContracts(), ...tokenContracts()],
): string {
  const lower = address.toLowerCase();
  return directory.find((c) => c.address && c.address.toLowerCase() === lower)?.label ?? address;
}

function asContractCall(entry: DebugLogEntry): ContractCall | null {
  if (entry.category !== "chain" || !entry.meta) return null;
  const { contract, method, txHash } = entry.meta;
  if (typeof contract !== "string" || typeof method !== "string") return null;
  return { contract, method, txHash: typeof txHash === "string" ? txHash : "" };
}

/** Every confirmed contract call logged at or after `sinceMs`. */
export function contractCallsSince(logs: DebugLogEntry[], sinceMs: number): ContractCall[] {
  return logs
    .filter((e) => Date.parse(e.timestamp) >= sinceMs)
    .map(asContractCall)
    .filter((c): c is ContractCall => c !== null);
}

/** Every confirmed contract call still in the log buffer. */
export function allContractCalls(logs: DebugLogEntry[]): ContractCall[] {
  return contractCallsSince(logs, Number.NEGATIVE_INFINITY);
}

export function describeContractCalls(calls: ContractCall[]): string {
  if (calls.length === 0) return "none (database only)";
  return calls.map((c) => `${c.contract}.${c.method}`).join(", ");
}

/**
 * Close a traced flow with the contracts it called. Pairs with a start
 * timestamp taken BEFORE the flow's first await, so no tx can predate it.
 */
export function logContractSummary(
  action: string,
  sinceMs: number,
  outcome: "done" | "failed",
): void {
  if (!debugCollectionAllowed()) return;
  try {
    const calls = contractCallsSince(useDebugStore.getState().logs, sinceMs);
    const prefix = outcome === "done" ? "contracts called" : "contracts called before failure";
    debugLog(outcome === "done" ? "info" : "warn", "tx", `── ${action} ${outcome} — ${prefix}: ${describeContractCalls(calls)} ──`, {
      contractsCalled: calls,
    });
  } catch {
    // Debug must not break tx flow
  }
}

/**
 * Start/summary bracket for call sites that cannot take useDebugTransaction
 * (it builds its own PolygonClient, too heavy for a list item).
 */
export async function traceContractCalls<T>(
  action: string,
  meta: Record<string, unknown>,
  fn: () => Promise<T>,
): Promise<T> {
  const since = Date.now();
  debugLog("info", "tx", `── ${action} started ──`, meta);
  try {
    const result = await fn();
    logContractSummary(action, since, "done");
    return result;
  } catch (err) {
    logContractSummary(action, since, "failed");
    throw err;
  }
}
